/**
 * The `Meta` sheet — a key/value bag by contract.
 *
 * design.md § 4 says Meta is "validated on write against a Zod schema in
 * `packages/shared` that lists the known keys and permits unknown ones to pass
 * through untouched — so an unrecognised key survives an upload-then-download
 * round trip instead of being silently dropped". That schema did not exist; the
 * backend agent found its absence in Phase 3. This is it.
 *
 * The recognised keys are the ones ONBOARDING.md § The workbook format lists.
 * Adding a key here is a documentation change, not a migration, because Meta is
 * a jsonb column.
 */
import { z } from 'zod';
import { STATE_ORDER, STATE_SCALE_DEFAULT, } from './model.js';
export const META_KEYS = [
    'Title',
    'Subtitle',
    'SnapshotDate',
    'ThemeMark',
    'ShutdownStart',
    'ShutdownEnd',
    'ShutdownLabel',
    'ContingencyStart',
    'ContingencyEnd',
    'TargetFinish',
    'TimelineRangeStart',
    'TimelineRangeEnd',
    // The per-version state scale — design.md § 7.9. Values are percentage
    // points. Absent keys fall back to STATE_SCALE_DEFAULT (0/40/90/100); the
    // existing published v1 is backfilled with 0 / 33 1/3 / 66 2/3 / 100.
    'StatePercentDraft',
    'StatePercentManuscript',
    'StatePercentProof',
    'StatePercentPublished',
];
/** The three marks the generator draws. An unknown mark renders nothing. */
export const THEME_MARKS = ['Skyline', 'Building', 'Abstract'];
/**
 * A date as it may appear in a workbook cell: an ISO string, a real Date, or an
 * Excel serial number. Stored as an ISO date string; parsing is the importer's
 * job, because only it knows the workbook's epoch.
 */
const metaDate = z.union([z.string().trim().min(1), z.date(), z.number()]);
const metaText = z.string().trim().max(500);
/**
 * A state percentage: 0-100, finite.
 *
 * A string is accepted because a workbook cell formatted as text arrives as
 * one, and refusing "40" where 40 works would be a surprise nobody could debug
 * from the spreadsheet. Coerced to a number so everything downstream sees a
 * number. Blank is not accepted — an empty cell should be an ABSENT key, which
 * falls back to the default, rather than a scale entry of zero.
 */
const statePercent = z
    .union([z.number(), z.string().trim().min(1)])
    .transform((v) => (typeof v === 'number' ? v : Number(v)))
    .refine((v) => Number.isFinite(v), { message: 'must be a number' })
    .refine((v) => v >= 0 && v <= 100, { message: 'must be between 0 and 100' });
/** The Meta key carrying each state's percentage. */
export const STATE_PERCENT_KEYS = {
    Draft: 'StatePercentDraft',
    Manuscript: 'StatePercentManuscript',
    Proof: 'StatePercentProof',
    Published: 'StatePercentPublished',
};
/**
 * `.passthrough()` is the load-bearing part: a client's own Meta key must
 * survive a round trip rather than being dropped because we did not recognise
 * it. Known keys are type-checked; everything else is carried untouched.
 */
export const metaSchema = z
    .object({
    Title: metaText.optional(),
    Subtitle: metaText.optional(),
    SnapshotDate: metaDate.optional(),
    ThemeMark: z.enum(THEME_MARKS).optional(),
    ShutdownStart: metaDate.optional(),
    ShutdownEnd: metaDate.optional(),
    ShutdownLabel: metaText.optional(),
    ContingencyStart: metaDate.optional(),
    ContingencyEnd: metaDate.optional(),
    TargetFinish: metaDate.optional(),
    TimelineRangeStart: metaDate.optional(),
    TimelineRangeEnd: metaDate.optional(),
    StatePercentDraft: statePercent.optional(),
    StatePercentManuscript: statePercent.optional(),
    StatePercentProof: statePercent.optional(),
    StatePercentPublished: statePercent.optional(),
})
    .passthrough();
/** The keys present that we do not recognise. Worth warning about, not rejecting. */
export function unknownMetaKeys(meta) {
    const known = new Set(META_KEYS);
    return Object.keys(meta).filter((k) => !known.has(k));
}
/* --------------------------------------------------------- the state scale */
/**
 * The state scale a version was published with, read from its Meta.
 *
 * Each key falls back INDEPENDENTLY to the default, so a workbook that sets
 * only StatePercentProof keeps 0/40/—/100 around it rather than losing the
 * whole scale to one typo. Values are assumed already validated by
 * `metaSchema`; anything unparseable here falls back rather than throwing,
 * because buildModel must not be the thing that fails on a bad cell.
 */
export function stateScaleFromMeta(meta) {
    const scale = { ...STATE_SCALE_DEFAULT };
    if (!meta)
        return scale;
    for (const state of STATE_ORDER) {
        const raw = meta[STATE_PERCENT_KEYS[state]];
        if (raw === undefined || raw === null || raw === '')
            continue;
        const n = typeof raw === 'number' ? raw : Number(String(raw).trim());
        if (Number.isFinite(n) && n >= 0 && n <= 100)
            scale[state] = n;
    }
    return scale;
}
/**
 * Non-ascending scales are warned about, not rejected.
 *
 * A plan where Proof is worth less than Manuscript is almost certainly a typo,
 * and a plan that is silently wrong is worse than one that complains. But it is
 * not incoherent — a client could reasonably decide two states are worth the
 * same — and refusing the whole import over it would fail an upload for a
 * judgement we are not entitled to make, with no way for the user to override
 * us. So it reads like every other workbook complaint: the import succeeds and
 * the warning bar says what looks wrong. Equal neighbouring values are allowed
 * silently; only a value LOWER than the state before it is flagged.
 *
 * Returns the warnings, in STATE_ORDER, empty when the scale ascends.
 */
export function stateScaleWarnings(scale) {
    const warnings = [];
    for (let i = 1; i < STATE_ORDER.length; i++) {
        const prev = STATE_ORDER[i - 1];
        const here = STATE_ORDER[i];
        if (scale[here] < scale[prev]) {
            warnings.push(`State percentages go backwards: ${STATE_PERCENT_KEYS[here]} is ${scale[here]}%, below ${STATE_PERCENT_KEYS[prev]} at ${scale[prev]}% — progress will appear to fall as work advances.`);
        }
    }
    return warnings;
}
//# sourceMappingURL=meta.js.map